import React, { useState, useEffect, useMemo, useRef } from 'react';
import { AppLayout } from '../../layouts/AppLayout';
import { PageHeader } from '../../layouts/PageHeader';
import { useCurrencyDashboard } from '../../../hooks/useCurrencyDashboard';
import {
  filterMonthlyData,
  computeKPIs,
  aggregateCurrencyBreakdown,
  monthLabelFromKey,
} from '../../../utils/currencyDashboardUtils';
import type { RevenueAmountMode } from '../../../utils/currencyDashboardUtils';
import { KpiStrip } from './KpiStrip';
import { CurrencyMixCard } from './CurrencyMixCard';
import { RevenueTrendSection } from './RevenueTrendSection';
import { DeepDiveSection } from './DeepDiveSection';

// ─── FY multi-select dropdown ─────────────────────────────────────────────────
const FYMultiDropdown: React.FC<{
  fyOptions: string[];
  selected: string[];
  onChange: (v: string[]) => void;
}> = ({ fyOptions, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const btnLabel = selected.length === 0 ? 'All Years' : selected.length === 1 ? selected[0] : `${selected.length} selected`;

  const toggle = (val: string) => {
    if (selected.includes(val)) onChange(selected.filter(v => v !== val));
    else onChange([...selected, val]);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 text-[12px] font-medium text-slate-700 bg-slate-100 border-0 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
      >
        {btnLabel}
        <svg className={`w-3 h-3 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-lg py-1 overflow-auto" style={{ minWidth: '11rem', maxHeight: '240px' }}>
          <label className="flex items-center gap-2 px-3 py-1.5 text-[12px] cursor-pointer hover:bg-slate-50">
            <input type="checkbox" className="accent-blue-600" checked={selected.length === 0}
              onChange={() => onChange([])} readOnly={selected.length === 0} />
            <span className="text-slate-600 font-medium">All Years</span>
          </label>
          <div className="border-t border-slate-100 my-1" />
          {fyOptions.map(fy => (
            <label key={fy} className="flex items-center gap-2 px-3 py-1.5 text-[12px] cursor-pointer hover:bg-slate-50">
              <input type="checkbox" className="accent-blue-600" checked={selected.includes(fy)} onChange={() => toggle(fy)} />
              <span className="text-slate-700">{fy}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Filter bar ───────────────────────────────────────────────────────────────
interface FilterBarProps {
  fyOptions: string[];
  selectedFYs: string[];
  onFYsChange: (v: string[]) => void;
  quarterOptions: Array<{ value: string; label: string }>;
  selectedQuarter: string;
  onQuarterChange: (v: string) => void;
  monthOptions: Array<{ value: string; label: string }>;
  selectedMonth: string;
  onMonthChange: (v: string) => void;
  revenueMode: RevenueAmountMode;
  onRevenueModeChange: (m: RevenueAmountMode) => void;
}

const RevenueBasisToggle: React.FC<{
  mode: RevenueAmountMode;
  onChange: (m: RevenueAmountMode) => void;
}> = ({ mode, onChange }) => (
  <div className="flex flex-wrap items-center gap-2">
    <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 shrink-0">
      Tax & deposit
    </span>
    <div
      className="inline-flex rounded-md border border-slate-200 bg-slate-100 p-0.5"
      role="group"
      aria-label="Revenue includes tax and deposit, or excludes them"
    >
      <button
        type="button"
        onClick={() => onChange('with_tax_deposit')}
        className={`px-2.5 py-1 rounded text-[11px] font-semibold transition-colors ${
          mode === 'with_tax_deposit' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
        }`}
      >
        With
      </button>
      <button
        type="button"
        onClick={() => onChange('ex_tax_ex_deposit')}
        className={`px-2.5 py-1 rounded text-[11px] font-semibold transition-colors ${
          mode === 'ex_tax_ex_deposit' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
        }`}
      >
        Without
      </button>
    </div>
  </div>
);

const FilterBar: React.FC<FilterBarProps> = ({
  fyOptions,
  selectedFYs,
  onFYsChange,
  quarterOptions,
  selectedQuarter,
  onQuarterChange,
  monthOptions,
  selectedMonth,
  onMonthChange,
  revenueMode,
  onRevenueModeChange,
}) => (
  <div className="flex items-center gap-2 flex-wrap justify-end">
    {/* FY selector */}
    <div className="flex items-center gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">FY</span>
      <FYMultiDropdown
        fyOptions={fyOptions}
        selected={selectedFYs}
        onChange={onFYsChange}
      />
    </div>

    <div className="h-4 w-px bg-slate-200" />

    {/* Quarter selector */}
    <div className="flex items-center gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">Q</span>
      <select
        value={selectedQuarter}
        onChange={e => onQuarterChange(e.target.value)}
        className="text-[12px] font-medium text-slate-700 bg-slate-100 border-0 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
      >
        {quarterOptions.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>

    <div className="h-4 w-px bg-slate-200" />

    {/* Month selector */}
    <div className="flex items-center gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">Month</span>
      <select
        value={selectedMonth}
        onChange={e => onMonthChange(e.target.value)}
        className="text-[12px] font-medium text-slate-700 bg-slate-100 border-0 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
      >
        {monthOptions.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>

    <div className="h-4 w-px bg-slate-200" />

    <RevenueBasisToggle mode={revenueMode} onChange={onRevenueModeChange} />
  </div>
);

// ─── Main page ────────────────────────────────────────────────────────────────
export const CurrencyDashboardPage: React.FC = () => {
  const { data, loading, error } = useCurrencyDashboard();
  const [revenueMode, setRevenueMode] = useState<RevenueAmountMode>('ex_tax_ex_deposit');

  // Derive available FYs
  const fyOptions = useMemo(() => {
    const fys = Array.from(new Set(data.map(d => d.financial_year_period))).sort().reverse();
    return fys;
  }, [data]);

  const [selectedFYs, setSelectedFYs] = useState<string[]>([]);
  const [selectedQuarter, setSelectedQuarter] = useState<string>('all');
  const [selectedMonth, setSelectedMonth] = useState<string>('all');

  // Auto-select latest FY on data load
  useEffect(() => {
    if (fyOptions.length > 0 && selectedFYs.length === 0) {
      setSelectedFYs([fyOptions[0]]);
    }
  }, [fyOptions, selectedFYs]);

  // Reset quarter/month when FYs change
  useEffect(() => {
    setSelectedQuarter('all');
    setSelectedMonth('all');
  }, [selectedFYs]);

  // Reset month when quarter changes
  useEffect(() => {
    setSelectedMonth('all');
  }, [selectedQuarter]);

  // Derive available quarters for the selected FYs
  const quarterOptions = useMemo(() => {
    const fyData = selectedFYs.length === 0 ? data : data.filter(d => selectedFYs.includes(d.financial_year_period));
    const qs = Array.from(new Set(fyData.map(d => d.financial_quarter))).sort();
    const qLabels: Record<number, string> = {
      1: 'Q1 (Apr-Jun)',
      2: 'Q2 (Jul-Sep)',
      3: 'Q3 (Oct-Dec)',
      4: 'Q4 (Jan-Mar)',
    };
    const options = [{ value: 'all', label: 'All Quarters' }];
    qs.forEach(q => options.push({ value: `q${q}`, label: qLabels[q] ?? `Q${q}` }));
    return options;
  }, [data, selectedFYs]);

  // Derive available months for the selected FYs + quarter
  // Sorted in Indian FY order (Apr first → Mar last) using the full yyyymm key
  const monthOptions = useMemo(() => {
    let slice = selectedFYs.length === 0 ? data : data.filter(d => selectedFYs.includes(d.financial_year_period));
    if (selectedQuarter !== 'all') {
      const q = parseInt(selectedQuarter.replace('q', ''), 10);
      slice = slice.filter(d => d.financial_quarter === q);
    }
    // Sort numerically: 202404 < 202405 … 202412 < 202501 … → Apr first, Mar last (correct Indian FY order)
    const months = Array.from(new Set(slice.map(d => d.invoice_month_key))).sort((a, b) => a - b);
    const options = [{ value: 'all', label: 'All Months' }];
    // Use full yyyymm key as value so filtering is year-precise (Apr '24 ≠ Apr '25)
    months.forEach(ym => options.push({ value: String(ym), label: monthLabelFromKey(ym) }));
    return options;
  }, [data, selectedFYs, selectedQuarter]);

  // Filtered data
  const filtered = useMemo(
    () => filterMonthlyData(data, selectedFYs, selectedQuarter, selectedMonth),
    [data, selectedFYs, selectedQuarter, selectedMonth]
  );

  // KPIs
  const kpis = useMemo(
    () =>
      computeKPIs(filtered, data, selectedFYs[0] ?? 'all', selectedQuarter, selectedMonth, revenueMode),
    [filtered, data, selectedFYs, selectedQuarter, selectedMonth, revenueMode]
  );

  // Currency breakdown for mix card
  const currencyBreakdown = useMemo(
    () => aggregateCurrencyBreakdown(filtered, revenueMode),
    [filtered, revenueMode]
  );

  // Period label for subtitle
  const periodLabel = useMemo(() => {
    if (selectedFYs.length === 0) return 'All Time';
    const fyLabel = selectedFYs.length === 1 ? `FY ${selectedFYs[0]}` : `FY ${selectedFYs.join(', ')}`;
    if (selectedQuarter !== 'all' && selectedMonth !== 'all') {
      const q = parseInt(selectedQuarter.replace('q', ''), 10);
      const qLabels: Record<number, string> = { 1: 'Q1', 2: 'Q2', 3: 'Q3', 4: 'Q4' };
      const mNum = parseInt(selectedMonth, 10);
      const monthPart =
        !Number.isNaN(mNum) && mNum >= 100000 ? monthLabelFromKey(mNum) : selectedMonth;
      return `${fyLabel} · ${qLabels[q] ?? selectedQuarter} · ${monthPart}`;
    }
    if (selectedQuarter !== 'all') {
      const q = parseInt(selectedQuarter.replace('q', ''), 10);
      const qLabels: Record<number, string> = { 1: 'Q1 (Apr-Jun)', 2: 'Q2 (Jul-Sep)', 3: 'Q3 (Oct-Dec)', 4: 'Q4 (Jan-Mar)' };
      return `${fyLabel} · ${qLabels[q] ?? selectedQuarter}`;
    }
    return fyLabel;
  }, [selectedFYs, selectedQuarter, selectedMonth]);

  if (error) {
    return (
      <AppLayout>
        <PageHeader title="Currency Dashboard" />
        <div className="p-6 flex items-center justify-center min-h-[40vh]">
          <div className="text-center">
            <p className="text-red-600 font-medium mb-1">Failed to load data</p>
            <p className="text-slate-500 text-sm">{error}</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageHeader
        title="Currency Dashboard"
        subtitle={periodLabel}
        filters={
          <FilterBar
            fyOptions={fyOptions}
            selectedFYs={selectedFYs}
            onFYsChange={setSelectedFYs}
            quarterOptions={quarterOptions}
            selectedQuarter={selectedQuarter}
            onQuarterChange={setSelectedQuarter}
            monthOptions={monthOptions}
            selectedMonth={selectedMonth}
            onMonthChange={setSelectedMonth}
            revenueMode={revenueMode}
            onRevenueModeChange={setRevenueMode}
          />
        }
      />

      {loading ? (
        <div className="p-6 flex items-center justify-center min-h-[40vh]">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <p className="text-slate-500 text-sm">Loading currency data…</p>
          </div>
        </div>
      ) : (
        <div className="p-6 space-y-4">
          {/* Top section: KPI cards (left) + Currency mix (right) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* KPI strip — left col */}
            <div>
              <KpiStrip kpis={kpis} />
            </div>
            {/* Currency mix — right col */}
            <div>
              <CurrencyMixCard entries={currencyBreakdown} periodLabel={periodLabel} />
            </div>
          </div>

          {/* Revenue trend section — full width */}
          <RevenueTrendSection filtered={filtered} all={data} revenueMode={revenueMode} />

          {/* Deep dive section — full width */}
          <DeepDiveSection
            filtered={filtered}
            all={data}
            fy={selectedFYs.length === 0 ? 'all' : selectedFYs}
            quarter={selectedQuarter}
            month={selectedMonth}
            revenueMode={revenueMode}
          />
        </div>
      )}
    </AppLayout>
  );
};

export default CurrencyDashboardPage;
